import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

interface Patient {
  id: string;
  patientId: string;
  name: string;
  dob?: string;
}

interface MyTask {
  id: string;
  accessionId: string;
  status: string;
  specimenClass: string;
  collectionDatetime: string;
  orderDate?: string;
  accessionedById?: string;
  assignedPathologistId?: string;
  grossingTechId?: string;
  createdBy?: string;
  patient?: Patient;
}

interface MyTasksData {
  orders: MyTask[];
  total: number;
  page: number;
  pageSize: number;
}

const MyTasks: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<MyTask[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMyTasks = async (pageNum = 0, pageSize = 10) => {
    setLoading(true);
    setError(null);
    try {
      const data: MyTasksData = await apiService.getMyTasks(pageNum + 1, pageSize);
      console.log('My Tasks API response:', data);
      setTasks(data.orders || []);
      setTotal(data.total || 0);
    } catch (e: any) {
      console.error('Error fetching my tasks:', e);
      setError(e.message || 'Failed to load your tasks');
      setTasks([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyTasks(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditOrder = (order: MyTask) => {
    navigate('/orders', { 
      state: { 
        openOrderId: order.id,
        accessionId: order.accessionId 
      } 
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'In Progress':
        return '🔄';
      case 'Completed / Finalized':
        return '✅';
      case 'Held':
        return '⏸️';
      case 'Pending QA Review':
        return '🔍';
      default:
        return '📋';
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'In Progress':
        return 'info';
      case 'Completed / Finalized':
        return 'success';
      case 'Held':
        return 'warning';
      case 'Pending QA Review':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getUserRole = (task: MyTask) => {
    const roles = [];
    // Check which role(s) the user has for this task
    // Note: We can't determine exact user ID here without auth context,
    // but the backend already filtered these as user's tasks
    if (task.accessionedById) roles.push('Accessioner');
    if (task.assignedPathologistId) roles.push('Pathologist');
    if (task.grossingTechId) roles.push('Grossing Tech');
    if (task.createdBy) roles.push('Creator');
    
    return roles.length > 0 ? roles.join(', ') : 'Assigned';
  };

  const formatDateTime = (datetime: string) => {
    if (!datetime) return '';
    return new Date(datetime).toLocaleString();
  };

  const refreshTasks = () => {
    fetchMyTasks(page, rowsPerPage);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" component="h1" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                My Tasks
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Orders assigned to you or created by you
              </Typography>
            </Box>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refreshTasks}
                disabled={loading}
                sx={{
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tasks Table */}
      <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : tasks.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No tasks assigned to you
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Tasks will appear here when orders are assigned to you as an accessioner, pathologist, or QA reviewer
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Accession ID</TableCell>
                      <TableCell>Patient</TableCell>
                      <TableCell>Specimen Class</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Your Role</TableCell>
                      <TableCell>Collection Date</TableCell>
                      <TableCell>Order Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tasks.map((task) => (
                      <TableRow key={task.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {task.accessionId}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {task.patient?.name || 'Unknown Patient'}
                            </Typography>
                            {task.patient?.patientId && (
                              <Typography variant="caption" color="text.secondary">
                                ID: {task.patient.patientId}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {task.specimenClass}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={<span>{getStatusIcon(task.status)}</span>}
                            label={task.status}
                            color={getStatusColor(task.status)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="primary">
                            {getUserRole(task)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(task.collectionDatetime)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(task.orderDate || '')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit Order">
                            <IconButton
                              size="small"
                              onClick={() => handleEditOrder(task)}
                              sx={{ color: '#1976d2' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  '& .MuiTablePagination-toolbar': {
                    paddingLeft: 2,
                    paddingRight: 2,
                  },
                }}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MyTasks;